'use client';

import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  lengthOptions,
  toneOptions
} from '@/app/[locale]/Ai-Paraphraser/Ai-Paraphraser-Functions/dropdownOptions';
import { handleGenerate } from '@/app/[locale]/Ai-Paraphraser/Ai-Paraphraser-Functions/Functions';
import {
  DropDownBox,
  StyledForm,
  StyledResults,
  StyledScrollableBox,
  StyledWrapper
} from '@/app/[locale]/Ai-Paraphraser/Ai-Paraphraser-Functions/PageStyling';
import { premiumFeatures } from '@/app/[locale]/Ai-Paraphraser/Ai-Paraphraser-Functions/premiumFeatures';
import CTABOX from '@/features/components/CTA/CtaBox';
import SmallPremiumAlert from '@/features/components/CTA/PremiumADSmall';
import Dropdown from '@/features/components/DropDown';
import TextInputWithWordCount from '@/features/components/Inputfields/TextInputWithWordCount';
import { getFlagCode, languageOptions } from '@/features/components/LANGUAGES_FLAGS';
import StyledMainBox from '@/features/components/MainBox';
import ResponseContainer from '@/features/components/resultDisplay/ResponseContainer';
import CustomButton from '@/features/components/SubmitButton';
import TabButtonGroup from '@/features/components/TabButtonGroup';
import { handlePaste } from '@/features/Functions/ResultFunctions';
import { useParaphraser } from '@/hooks/useParaphraser';

const FormPage = () => {
  const intl = useIntl();
  const [selectedTab, setSelectedTab] = useState<'result' | 'history'>('result');
  const {
    placeholderText,
    inputText,
    setInputText,
    tone,
    setTone,
    length,
    setLength,
    language,
    setLanguage,
    fingerPrint,
    setFingerPrint,
    loading,
    setLoading,
    premium,
    setPremium,
    responses,
    setResponses,
    streamingResponse,
    setStreamingResponse,
    isStreaming,
    setIsStreaming,
    counter,
    setCounter,
    isTouched,
    setIsTouched,
    showAd,
    setShowAd,
    showDropdown,
    setShowDropdown,
    inputRef
  } = useParaphraser();
  const { formatMessage } = useIntl();

  const handleBlur = () => {
    setIsTouched(true);
  };
  const handleTabChange = (tab: 'result' | 'history') => {
    setSelectedTab(tab);
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputText.trim()) {
      setIsTouched(true); // Mark the field as touched to show error
      setTimeout(() => {
        inputRef.current?.focus(); // Auto-focus the field
      }, 0);
      return;
    }
    setShowAd(false);
    await handleGenerate(
      e,
      inputText,
      counter,
      tone,
      length,
      language,
      fingerPrint,
      setLoading,
      setIsStreaming,
      setStreamingResponse,
      setResponses,
      setCounter,
      setInputText
    );
  };

  return (
    <StyledMainBox>
      <StyledWrapper>
        {/* Right Side: Form */}
        <StyledForm onSubmit={onSubmit}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Title */}
            <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              <FormattedMessage id="home.text-Paraphraser.title" />
            </Typography>
          </Box>
          <TextInputWithWordCount
            placeholderText={formatMessage({ id: 'home.text-Paraphraser.placeholder' })}
            inputText={inputText}
            setInputText={setInputText}
            isTouched={isTouched}
            handlePaste={handlePaste}
          />
          {/* Settings Button */}
          <DropDownBox>
            {/* Language Dropdown */}
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.language.dropdown' })}
              value={language}
              onChange={e => setLanguage(e.target.value)}
              options={languageOptions(intl, getFlagCode)}
            />

            {/* Tone Dropdown */}
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.tone.dropdown' })}
              value={tone}
              onChange={e => setTone(e.target.value)}
              options={toneOptions(intl, premium)} // Use the externalized options
              premium={premium}
            />
            {/* Language Dropdown */}
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.length.dropdown' })}
              value={length}
              onChange={e => setLength(e.target.value)}
              options={lengthOptions(intl)} // Use the externalized options
            />
          </DropDownBox>

          <CustomButton
            disabled={isStreaming || loading}
            counter={counter}
            maxCount={3}
            loading={false}
            premium={premium}
            label={formatMessage({ id: 'home.text-Paraphraser.button' })} // Get translated string
          />
        </StyledForm>

        {/* Left Side: Results */}
        <StyledResults>
          <TabButtonGroup
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
            responsesCount={responses.length}
          />

          <StyledScrollableBox>
            <ResponseContainer
              placeholderText={formatMessage({ id: 'Result.text-Paraphraser.placeholder' })}
              responses={responses}
              setResponses={setResponses}
              handleTabChange={handleTabChange}
              selectedTab={selectedTab}
              streamingResponse={streamingResponse}
              isStreaming={isStreaming}
              premium={premium}
            />
          </StyledScrollableBox>
        </StyledResults>
      </StyledWrapper>
      <CTABOX>
        <SmallPremiumAlert
          title={<FormattedMessage id="home.premium.title" />}
          features={premiumFeatures} // Pass the externalized features
        />
      </CTABOX>
    </StyledMainBox>
  );
};

export default FormPage;
